"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Phonetic = {
  text?: string;
  audio?: string;
};

type Meaning = {
  partOfSpeech?: string;
  synonyms?: string[];
  antonyms?: string[];
  definitions: Array<{
    definition: string;
    example?: string;
    synonyms?: string[];
    antonyms?: string[];
  }>;
};

type DictionaryEntry = {
  word: string;
  phonetic?: string;
  phonetics?: Phonetic[];
  meanings?: Meaning[];
  origin?: string;
  sourceUrls?: string[];
};

const sanitizeQuery = (q: string) => q.replace(/[^a-zA-Z '\-]/g, "").trim();

export default function Dictionary() {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DictionaryEntry[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentAudioSrcRef = useRef<string | null>(null);
  const lastPlayIdRef = useRef(0);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  // Pull selected text like TTS, but avoid overriding when user is typing
  useEffect(() => {
    function handleSelectionChange() {
      if (isFocused) return;
      const sel = window.getSelection?.();
      if (!sel) return;
      const anchor = sel.anchorNode as Node | null;
      const focus = sel.focusNode as Node | null;
      const container = resultsRef.current;
      if (container) {
        // Ignore selection changes that occur within the results panel
        if (
          (anchor && container.contains(anchor)) ||
          (focus && container.contains(focus))
        ) {
          return;
        }
      }
      const value = sanitizeQuery(sel.toString() ?? "");
      if (value && value.length > 0) {
        setQuery(value);
      }
    }
    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, [isFocused]);

  const primaryPhonetic = useMemo(() => {
    if (!result || result.length === 0) return undefined;
    const entry = result[0];
    const phoneticText =
      entry.phonetic || entry.phonetics?.find((p) => p.text)?.text;
    const audioUrl = entry.phonetics?.find((p) => p.audio)?.audio;
    return { text: phoneticText, audio: audioUrl } as Phonetic | undefined;
  }, [result]);

  const aggregates = useMemo(() => {
    const aggSynonyms = new Set<string>();
    const aggAntonyms = new Set<string>();
    let originText: string | undefined;
    if (result && result.length > 0) {
      for (const entry of result) {
        if (!originText && entry.origin) originText = entry.origin;
        for (const m of entry.meanings ?? []) {
          for (const s of m.synonyms ?? []) aggSynonyms.add(s);
          for (const a of m.antonyms ?? []) aggAntonyms.add(a);
          for (const d of m.definitions ?? []) {
            for (const s of d.synonyms ?? []) aggSynonyms.add(s);
            for (const a of d.antonyms ?? []) aggAntonyms.add(a);
          }
        }
      }
    }
    return {
      synonyms: Array.from(aggSynonyms).sort((a, b) => a.localeCompare(b)),
      antonyms: Array.from(aggAntonyms).sort((a, b) => a.localeCompare(b)),
      origin: originText,
    };
  }, [result]);

  const canSearch = sanitizeQuery(query).length > 0;

  const playAudio = useCallback(async () => {
    const src = primaryPhonetic?.audio;
    if (!src) return;
    try {
      const audio = (audioRef.current ||= new Audio());
      lastPlayIdRef.current += 1;
      const playId = lastPlayIdRef.current;

      // If new src, reset and wait until it can play before calling play
      if (currentAudioSrcRef.current !== src) {
        audio.pause();
        audio.currentTime = 0;
        currentAudioSrcRef.current = src;
        audio.src = src;
        audio.load();
        await new Promise<void>((resolve, reject) => {
          const onCanPlay = () => {
            cleanup();
            resolve();
          };
          const onError = () => {
            cleanup();
            reject(new Error("audio error"));
          };
          const cleanup = () => {
            audio.removeEventListener("canplay", onCanPlay);
            audio.removeEventListener("error", onError);
          };
          audio.addEventListener("canplay", onCanPlay, { once: true });
          audio.addEventListener("error", onError, { once: true });
          if (audio.readyState >= 2) {
            cleanup();
            resolve();
          }
        });
      } else {
        // same src: restart cleanly
        audio.pause();
        audio.currentTime = 0;
      }

      if (playId !== lastPlayIdRef.current) return;
      await audio.play().catch(() => {});
    } catch {}
  }, [primaryPhonetic]);

  // Pause audio when definitions change or on unmount to prevent race conditions
  useEffect(() => {
    if (!result) return;
    try {
      audioRef.current?.pause();
    } catch {}
  }, [result]);

  useEffect(() => {
    return () => {
      try {
        audioRef.current?.pause();
      } catch {}
    };
  }, []);

  const fetchDefinitions = useCallback(async (term: string) => {
    const word = sanitizeQuery(term);
    if (!word) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const url = `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(
        word
      )}`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) {
        // API returns 404 for not found words
        if (res.status === 404) {
          setResult([]);
          return;
        }
        throw new Error(`Request failed: ${res.status}`);
      }
      const data = (await res.json()) as DictionaryEntry[];
      setResult(data);
    } catch (err) {
      if (
        err &&
        typeof err === "object" &&
        (err as { name?: string }).name === "AbortError"
      )
        return;
      setError("Unable to fetch definition. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search on input changes
  useEffect(() => {
    const term = sanitizeQuery(query);
    if (!term) {
      setResult(null);
      setError(null);
      return;
    }
    const t = setTimeout(() => {
      void fetchDefinitions(term);
    }, 400);
    return () => clearTimeout(t);
  }, [query, fetchDefinitions]);

  return (
    <div className=" flex h-full w-full flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <label htmlFor="dictionary-search" className="sr-only">
          Search word
        </label>
        <input
          id="dictionary-search"
          type="text"
          className="min-w-[220px] flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          placeholder="Type or select a word to look up..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <button
          className="h-9 rounded-md bg-accent px-4 text-sm text-accent-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => canSearch && fetchDefinitions(query)}
          disabled={!canSearch || loading}
          aria-disabled={!canSearch || loading}
        >
          Search
        </button>
      </div>

      {primaryPhonetic?.text || primaryPhonetic?.audio ? (
        <div className="flex items-center gap-3 text-sm text-foreground/80">
          {primaryPhonetic?.text ? (
            <span className="rounded bg-surface px-2 py-1">
              {primaryPhonetic.text}
            </span>
          ) : null}
          {primaryPhonetic?.audio ? (
            <button
              className="h-8 rounded-md bg-surface px-3 text-xs hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              onClick={playAudio}
              aria-label="Play pronunciation"
            >
              Play
            </button>
          ) : null}
        </div>
      ) : null}

      {aggregates.origin ? (
        <div className="text-xs text-foreground/70 -mt-1">
          Origin: {aggregates.origin}
        </div>
      ) : null}

      <div
        ref={resultsRef}
        className="no-drag relative flex-1 overflow-auto rounded-lg border border-border bg-background no-drag cursor-text select-text"
      >
        {loading ? (
          <div className="p-4 text-sm">Loading…</div>
        ) : error ? (
          <div className="p-4 text-sm text-red-500">{error}</div>
        ) : result == null ? (
          <div className="p-4 text-sm text-foreground/70">
            Enter a word or select text on the page to look up.
          </div>
        ) : result.length === 0 ? (
          <div className="p-4 text-sm">No definitions found.</div>
        ) : (
          <div className="p-3 space-y-4">
            {result.map((entry, idx) => (
              <div key={`${entry.word}-${idx}`} className="space-y-2">
                <div className="text-base font-semibold">{entry.word}</div>
                {entry.meanings?.map((m, i) => (
                  <div key={`${entry.word}-m-${i}`} className="space-y-1">
                    {m.partOfSpeech ? (
                      <div className="text-xs uppercase tracking-wide text-foreground/60">
                        {m.partOfSpeech}
                      </div>
                    ) : null}
                    <ol className="list-decimal pl-5 space-y-2">
                      {m.definitions.map((d, di) => (
                        <li
                          key={`${entry.word}-d-${di}`}
                          className="text-sm leading-6"
                        >
                          <div>{d.definition}</div>
                          {d.example ? (
                            <div className="mt-1 text-xs text-foreground/70">
                              “{d.example}”
                            </div>
                          ) : null}
                          {d.synonyms && d.synonyms.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {d.synonyms.slice(0, 10).map((s) => (
                                <button
                                  key={`${entry.word}-syn-${di}-${s}`}
                                  className="rounded-full bg-surface px-2 py-0.5 text-xs text-foreground/80 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                  title="Search this synonym"
                                  onClick={() => {
                                    setQuery(s);
                                    void fetchDefinitions(s);
                                  }}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
                {entry.sourceUrls && entry.sourceUrls.length > 0 ? (
                  <div className="pt-1 text-xs text-foreground/60">
                    Source: {entry.sourceUrls[0]}
                  </div>
                ) : null}
              </div>
            ))}
            {(aggregates.synonyms.length > 0 ||
              aggregates.antonyms.length > 0) && (
              <div className="pt-2 space-y-3">
                {aggregates.synonyms.length > 0 ? (
                  <div>
                    <div className="mb-1 text-xs uppercase tracking-wide text-foreground/60">
                      Synonyms
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {aggregates.synonyms.slice(0, 40).map((s) => (
                        <button
                          key={`agg-syn-${s}`}
                          className="rounded-full bg-surface px-2 py-0.5 text-xs text-foreground/80 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          title="Search this synonym"
                          onClick={() => {
                            setQuery(s);
                            void fetchDefinitions(s);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {aggregates.antonyms.length > 0 ? (
                  <div>
                    <div className="mb-1 text-xs uppercase tracking-wide text-foreground/60">
                      Antonyms
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {aggregates.antonyms.slice(0, 40).map((a) => (
                        <button
                          key={`agg-ant-${a}`}
                          className="rounded-full bg-surface px-2 py-0.5 text-xs text-foreground/80 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          title="Search this antonym"
                          onClick={() => {
                            setQuery(a);
                            void fetchDefinitions(a);
                          }}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
